import { Injectable } from '@angular/core';
import { InAppBrowser, InAppBrowserObject } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { AlertController, LoadingController, ToastController, ToastOptions } from '@ionic/angular';
import { GraphQLError } from 'graphql';
import moment from 'moment';
import { settings } from 'src/app/settings';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';

export type PrimitiveValue = string | number | boolean | null;

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor(
    private inAppBrowser: InAppBrowser,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private toastController: ToastController,
  ) {}

  /**
   * @deprecated applies mixins to a class constructor (example: `applyMixins(JobsPage, [JobsMixin])`)
   * This method is deprecated. DO NOT USE.
   * @param derivedCtor - null.
   * @param baseCtors - null.
   */
  applyMixins(derivedCtor: any, baseCtors: any[]) {
    baseCtors.forEach((baseCtor) => {
      Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
        derivedCtor.prototype[name] = baseCtor.prototype[name];
      });
    });
  }

  /**
   * Capitalizes the given string.
   *
   * @param string - string to capitalize.
   * @returns capitalized string.
   */
  public capitalize(string: string): string {
    return string.toLowerCase().replace(/^./, string[0].toUpperCase());
  }

  /**
   * Opens a link externally.
   * This method wraps the inAppBrowser method, which is the supported way to open external links.
   *
   * @param url - url to open.
   * @param target
   * @param options - InAppBrowserOptions, default to location=yes.
   */
  public openExternalLink(url: string, target = '_system', options = 'location=yes'): InAppBrowserObject {
    return this.inAppBrowser.create(url, target, options);
  }

  public async showLoading(message?: string): Promise<any> {
    message = message || 'Loading...';

    const loader = await this.loadingCtrl.create({
      message,
    });

    await loader.present();
    return loader;
  }

  public async hideLoading(loader) {
    if (loader) {
      return await loader.dismiss();
    }
  }

  // TODO - Replace base page methods with Util methods

  /**
   * Method to show alert.
   *
   * @param title - The main title in the heading of the alert
   * @param message - The main message to be displayed in the alert
   * @param handler - Handler to handle action on "Ok" button click
   */
  public async showAlert(title: string, message?: string, handler?: () => any) {
    const alert = await this.alertCtrl.create({
      header: title,
      message,
      buttons: [{ text: 'Ok', handler: handler || (() => {}) }],
    });
    await alert.present();
    return alert;
  }

  public showErrorAlert(message: string, handler?: () => any) {
    return this.errorAlert(message, handler).then((alert) => {
      alert.present();
    });
  }

  /**
   * Show error alert when Sendbird operations fail due to timeout.
   * This provides a consistent user experience across all timeout errors.
   *
   * @param retryHandler - Callback function to execute when user clicks "Try again" button
   */
  public async showTimeoutErrorAlert(retryHandler: () => void): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Loading error',
      message: 'Something went wrong. Please try again.',
      buttons: [
        {
          text: 'Try again',
          handler: retryHandler,
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
  }

  private errorAlert(message: string, handler?: () => any) {
    return this.alertCtrl.create({
      header: 'Error',
      message,
      buttons: [{ text: 'Ok', handler: handler || (() => {}) }],
    });
  }

  /**
   * Returns the graphQL Error Message by taking into account if the error message can be displayed to the user based
   * on the display_message field. If the graphQL error message can't be returned, simply return a standard error.
   *
   * @deprecated Replaced by {@link getGraphQLErrorText} from *shared/utilities/graphql-error-handling.ts*
   * @param errorObject - The array that is returned when there is a graphQL error.
   * @returns error to be displayed.
   */
  public getGraphQlError(errorObject: GraphQLError[] | { graphQLErrors: GraphQLError[] }): string {
    return getGraphQLErrorText(errorObject);
  }

  /**
   * Parses and return an object for given data string.
   *
   * @param data - Stringified json.
   */
  getParsedRawData(data: string) {
    if (!data) {
      return {};
    }

    try {
      return JSON.parse(data) || {};
    } catch {
      return {};
    }
  }

  /**
   * Convert an object (map) of arrays of named objects into a flat array of named objects,
   * to simplify the structure for further use in the component template.
   * Example:
   *   From: { "arr1": [{ "name": "a" }], "arr2": [{"name": "b"}, {"name": "c"}] }
   *   To: [{ "name": "a" }, {"name": "b"}, {"name": "c"}]
   *
   * @param mapOfArraysOfNamedObjects - object (map) of arrays of named objects
   */
  static flattenNamedObjectArray(mapOfArraysOfNamedObjects: { [key: string]: { name: string }[] }): { name: string }[] {
    return Object.values(mapOfArraysOfNamedObjects).reduce(
      (NamedObjects, NamedObject) => NamedObjects.concat(NamedObject),
      [],
    );
  }

  /**
   * Display Success toast
   *
   * @param message - success message to display in toast
   * @param showAsAlert - adds alert role to the toast when set to true, forcing voiceovers to read alert text out loud
   */
  async successToast(message: string, showAsAlert?: boolean) {
    const toastOptions: ToastOptions = {
      message,
      duration: 2000,
      cssClass: 'success-toast',
    };
    if (showAsAlert) {
      toastOptions.htmlAttributes = {
        role: 'alert',
      };
    }
    const toast = await this.toastController.create(toastOptions);

    await toast.present();
  }

  /**
   * Returns an ion-datetime compatible datetime object
   *
   * @param time - the 24hrs string value e.g. 23:30.
   */
  convert24HrsToIonicDatetime(time: string) {
    if (time) {
      return moment(time, 'HH:mm').format(settings.ionicDateTimeFormat);
    }
    return null;
  }

  /**
   * Get the form element with error using .invalid class and scroll to this element.
   */
  scrollToError(): void {
    const firstElementWithError: HTMLElement = document.querySelector('.invalid');

    if (firstElementWithError) {
      firstElementWithError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Returns an ion-datetime compatible datetime object
   *
   * @param datetime
   * @param time - the 24hrs string value e.g. 23:30.
   */
  convert12HrsDatetimeToIonicDatetime(time) {
    return time ? moment(time, 'mm/DD/YYYY hh:mm a').format(settings.ionicDateTimeFormat) : null;
  }

  /**
   * Converts ion-datetime to 24hr format
   *
   * @param time - Datetime in ionic format.
   */
  convertIonicDatetimeTo24Hours(time) {
    return time ? moment(time).format('HH:mm') : null;
  }

  /**
   * Converts ion-datetime to 12hr format
   *
   * @param time - Datetime in ionic format.
   */
  convertIonicDatetimeTo12Hours(time) {
    return time ? moment(time).format('hh:mm a') : null;
  }

  /**
   * Given an object and an array of keys that match the properties of the object,
   * return a new object with only the given keys.
   *
   * @param myObj - Object to retrieve the properties from.
   * @param keys - Array containing keys from the object.
   * @returns new Object with only properties matching those from the keys array.
   */
  pickValuesFromObject<T, K extends keyof T>(myObj: T, keys: Array<K>): Partial<T> {
    return Object.keys(myObj).reduce((prev: Partial<T>, key: string) => {
      if (!keys.includes(<K>key)) return prev;

      return {
        ...prev,
        [key]: myObj[key],
      };
    }, {} as Partial<T>);
  }

  /**
   * Type guard to check if a value is a primitive.
   *
   * @param value - The value to check.
   * @returns True if the value is a primitive, false otherwise.
   */
  private isPrimitiveValue(value: unknown): value is PrimitiveValue {
    return (
      value === null ||
      value === undefined ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    );
  }

  /**
   * Converts object keys from camelCase to snake_case.
   *
   * @param obj - The object to transform.
   */
  transformKeysToSnakeCase(obj: Record<string, unknown> | undefined): Record<string, PrimitiveValue> {
    if (!obj || typeof obj !== 'object') {
      return {};
    }

    const result: Record<string, PrimitiveValue> = {};

    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (this.isPrimitiveValue(value)) {
        const snakeCaseKey = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
        acc[snakeCaseKey] = value;
      }
      return acc;
    }, result);
  }
}
